import React, { useState, useEffect } from 'react';
import { Building2, Upload, Info, LogOut } from 'lucide-react';
import SingleVerification from './components/SingleVerification';
import BatchVerification from './components/BatchVerification';
import Modal from './components/Modal';
import PasswordPrompt from './components/PasswordPrompt';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import About from './components/About';
import LanguageSelector from './components/LanguageSelector';
import { LocalizationProvider, useLocalization } from './contexts/LocalizationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Analytics } from './utils/analytics';

type TabType = 'single' | 'batch' | 'about';
type ModalType = 'terms' | 'privacy' | null;

const AppContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('single');
  const [modalType, setModalType] = useState<ModalType>(null);
  const { isAuthenticated, isEmailVerified, logout, loading } = useAuth();
  const { t } = useLocalization();

  // Track page load
  useEffect(() => {
    const startTime = Date.now();
    const timer = setTimeout(() => {
      Analytics.pageLoad('main_app', Date.now() - startTime);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Track tab changes
  const handleTabChange = (newTab: TabType) => {
    if (newTab !== activeTab) {
      Analytics.navigationChange(activeTab, newTab);
      setActiveTab(newTab);
    }
  };

  // Track modal events
  const handleModalOpen = (type: ModalType) => {
    if (type) {
      Analytics.modalOpen(type);
      setModalType(type);
    }
  };

  const handleModalClose = () => {
    if (modalType) {
      Analytics.modalClose(modalType);
      setModalType(null);
    }
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show password prompt if not authenticated or email not verified
  if (!isAuthenticated || !isEmailVerified) {
    return <PasswordPrompt />;
  }

  const tabs = [
    { id: 'single' as TabType, label: t.navigation.singleVerification, icon: Building2 },
    { id: 'batch' as TabType, label: t.navigation.batchVerification, icon: Upload },
    { id: 'about' as TabType, label: t.navigation.about, icon: Info }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'single':
        return <SingleVerification />;
      case 'batch':
        return <BatchVerification />;
      case 'about':
        return <About />;
      default:
        return <SingleVerification />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                {t.header.title}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                {t.header.subtitle}
              </div>
              <LanguageSelector />
              <button
                onClick={logout}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                title={t.navigation.logout}
              >
                <LogOut className="w-4 h-4" />
                {t.navigation.logout}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>
              {t.footer.description}
            </p>
            <p className="mt-1">
              {t.footer.disclaimer}
            </p>
            <div className="mt-4 flex justify-center space-x-6">
              <button
                onClick={() => handleModalOpen('terms')}
                className="text-primary-600 hover:text-primary-700 underline transition-colors"
              >
                {t.footer.termsOfUse}
              </button>
              <button
                onClick={() => handleModalOpen('privacy')}
                className="text-primary-600 hover:text-primary-700 underline transition-colors"
              >
                {t.footer.privacyPolicy}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <Modal
        isOpen={modalType !== null}
        onClose={handleModalClose}
        title={modalType === 'terms' ? t.footer.termsOfUse : t.footer.privacyPolicy}
      >
        {modalType === 'terms' ? <TermsOfUse /> : <PrivacyPolicy />}
      </Modal>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <LocalizationProvider>
        <AppContent />
      </LocalizationProvider>
    </AuthProvider>
  );
};

export default App;
